/** Numeric sort for score-like string values (e.g. "13" before "123"). */

const EMPTY_SCORE_TOKENS = new Set(["", "-", "--", "--/--", "n/a", "na", "null", "undefined"]);

export const parseNumericScore = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value).trim();
  if (!raw || EMPTY_SCORE_TOKENS.has(raw.toLowerCase())) {
    return null;
  }

  const cleaned = raw.replace(/%/g, "").replace(/,/g, "").trim();
  const firstPart = cleaned.split("/")[0].trim();
  const parsed = Number(firstPart);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isScoreSortField = (field: string): boolean =>
  /score|obtained_marks|max_marks|percentage|cgpa/i.test(field);

export const compareNumericScore = (
  a: unknown,
  b: unknown,
  direction: string
): number => {
  const isAsc = direction === "asc" || direction === "ascending";
  const aNum = parseNumericScore(a);
  const bNum = parseNumericScore(b);
  const aEmpty = aNum === null;
  const bEmpty = bNum === null;

  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return isAsc ? -1 : 1;
  if (bEmpty) return isAsc ? 1 : -1;
  return isAsc ? aNum - bNum : bNum - aNum;
};

/** Prefer numeric order when both values parse as numbers; otherwise localeCompare. */
export const compareMaybeNumeric = (
  a: unknown,
  b: unknown,
  direction: string
): number => {
  const aNum = parseNumericScore(a);
  const bNum = parseNumericScore(b);
  if (aNum !== null && bNum !== null) {
    return compareNumericScore(a, b, direction);
  }

  const isAsc = direction === "asc" || direction === "ascending";
  const aStr = a == null ? "" : String(a);
  const bStr = b == null ? "" : String(b);
  const cmp = aStr.localeCompare(bStr, undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return isAsc ? cmp : -cmp;
};
